import { create } from 'zustand';
import { supabase } from '@/integrations/supabase/client';
import { Chat, Message, AIModel, Attachment } from '@/types';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/use-toast';
import { v4 as uuidv4 } from 'uuid';
import { sendMessageToModel } from '@/services/ModelService';
import { useKnowledgeBaseSearch } from './chat/useKnowledgeBaseSearch';
import { useAppStore } from '@/store';
import { User } from '@supabase/supabase-js';

interface SupabaseChatState {
  chatId?: string;
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  chatTitle: string;
  modelName: string;
  selectedModelId: string | null;
  modelId2?: string | null;
  isUsingKnowledgeBase: boolean;
  isMemoryUpdating: boolean;
  initializeChat: (chatId: string) => void;
  sendMessage: (content: string, attachments?: Attachment[], kbId?: string | null, kbName?: string | null, user?: User) => Promise<void>;
  handleTitleChange: (newTitle: string) => void;
  handleDeleteChat: () => void;
  handleModelChange: (modelId: string) => void;
  handleModel2Change: (modelId: string) => void;
  clearMessages: () => void;
  setMessages: (messages: Message[]) => void;
}

const MEMORY_THRESHOLD = 10; // Number of new messages before considering summarization
const RECENT_MESSAGES_COUNT = 20; // Number of recent messages to always keep in context

// Helper to clean up model output: remove role labels and truncate at first fake 'USER:' turn
function sanitizeModelOutput(text: string): string {
  if (!text) return text;
  const userTurnIdx = text.search(/\n?\s*USER:/i);
  if (userTurnIdx >= 0) {
    text = text.slice(0, userTurnIdx);
  }
  const cleaned = text
    .split('\n')
    .filter((line) => !/^\s*(USER|ASSISTANT|SYSTEM):/i.test(line))
    .join('\n')
    .trim();
  return cleaned || text.trim();
}

export const useSupabaseChatStore = create<SupabaseChatState>((set, get) => {
  const { findKnowledgeBaseInContent, performKnowledgeBaseSearch } = useKnowledgeBaseSearch();

  return {
    chatId: undefined,
    messages: [],
    isLoading: false,
    error: null,
    chatTitle: '',
    modelName: '',
    selectedModelId: null,
    modelId2: null,
    isUsingKnowledgeBase: false,
    isMemoryUpdating: false,

    initializeChat: async (chatId) => {
      const { chats, fetchChatMessages, fetchSingleChat, fetchModels, models: allModelsFromStore } = useSupabaseStore.getState();
      
      let allModels = allModelsFromStore;
      // Ensure models are fetched from the main store
      if (allModels.length === 0) {
        await fetchModels();
        allModels = useSupabaseStore.getState().models;
      }

      let chat = chats.find(c => c.id === chatId);

      if (!chat) {
        chat = await fetchSingleChat(chatId);
        if (!chat) {
          toast({ title: "Error", description: "Chat not found or could not be loaded.", variant: "destructive" });
          return;
        }
      }

      if (chat) {
        const model = allModels.find(m => m.id === chat.modelId);
        
        // Always fetch messages to ensure we have the latest data including Arena messages
        if (!chat.messagesLoaded) {
          await fetchChatMessages(chatId);
          // Get the updated chat after fetching messages
          const updatedChat = useSupabaseStore.getState().chats.find(c => c.id === chatId);
          if (updatedChat) {
            chat = updatedChat;
          }
        }

        // Ensure Arena message fields are properly preserved
        const structuredMessages = (chat.messages || []).map(msg => ({
          ...msg,
          is_arena: msg.is_arena || false,
          arena_group_id: msg.arena_group_id || undefined,
          arena_model: msg.arena_model || undefined,
          arena_order: msg.arena_order || undefined,
        }));

        set({
          chatId,
          messages: structuredMessages,
          chatTitle: chat.title,
          selectedModelId: chat.modelId,
          modelId2: chat.modelId2, // Initialize modelId2 from chat data
          modelName: model?.name || 'Unknown Model',
        });
      }
    },

    setMessages: (messages) => {
      set({ messages });
    },

    sendMessage: async (content, attachments, kbIdFromInput, kbNameFromInput, user) => {
      const { chatId, selectedModelId, messages: currentMessages } = get();
      const { models } = useSupabaseStore.getState(); // Get models from the main store
      const { addMessage: addMessageToDb, addMessageLocally, updateMessageLocally, updateChat } = useSupabaseStore.getState();
      
      if (!user || !chatId || !selectedModelId) {
        toast({ title: "Error", description: "Cannot send message. User not logged in or chat not fully initialized.", variant: "destructive" });
        set({ isLoading: false });
        return;
      }
      
      const selectedModel = models.find(m => m.id === selectedModelId);
      if (!selectedModel) {
        toast({ title: "Error", description: "Selected AI model not found.", variant: "destructive" });
        set({ isLoading: false });
        return;
      }
      
      set({ isLoading: true, error: null });
      
      let historyForAI: Message[] = [...currentMessages];
      let userQuery = content;

      // Inject guardrails for local models to avoid roleplay/self-dialogue
      if (selectedModel && selectedModel.provider === 'local') {
        const guardrailsMessage: Message = {
          id: uuidv4(),
          chatId,
          role: 'system',
          sender: 'system',
          timestamp: new Date().toISOString(),
          content:
            "Strict output rules:\n" +
            "- Answer the user's last message directly.\n" +
            "- Do NOT include lines starting with USER:, ASSISTANT:, or SYSTEM:.\n" +
            "- Do NOT invent or simulate the user's reply.\n" +
            "- If you ask a follow-up question, end your message after the question and wait for a response.",
        };
        historyForAI.unshift(guardrailsMessage);
      }
      
      const currentChat = useSupabaseStore.getState().chats.find(c => c.id === chatId);
      const lastSummarizedCount = currentChat?.lastSummarizedTotalMessagesCount || 0;

      // Trigger summarization only if enough new messages have accumulated
      if (currentMessages.length - lastSummarizedCount >= MEMORY_THRESHOLD) {
        set({ isMemoryUpdating: true });

        try {
          const messagesToSummarize = historyForAI.slice(0, -RECENT_MESSAGES_COUNT); // Summarize older messages
          const recentMessages = historyForAI.slice(-RECENT_MESSAGES_COUNT); // Keep recent messages in direct context

          const summaryPrompt = "You are a memory assistant. Summarize the following conversation concisely. Extract key facts, decisions, and topics. This summary will be used as a memory for an ongoing chat. Output only the summary. CONVERSATION:\n" + 
            messagesToSummarize.map(m => `${m.role}: ${m.content}`).join('\n');
          
          const summaryRequest: Message[] = [{
              id: uuidv4(),
              chatId: chatId,
              role: 'user',
              sender: 'user',
              content: summaryPrompt,
              timestamp: new Date().toISOString(),
          }];

          const summarizationModel = models.find(m => m.apiName === 'llama3') || selectedModel;
          const summaryResponse = await sendMessageToModel(summaryRequest, summarizationModel, models);

          if (summaryResponse.content.startsWith('Error:')) {
            throw new Error(summaryResponse.content);
          }

          const summaryMessage: Message = {
              id: uuidv4(),
              role: 'system',
              content: `[Memory] This is a summary of the conversation so far: ${summaryResponse.content}`,
              sender: 'system',
              timestamp: new Date().toISOString(),
              chatId,
          };

          historyForAI = [summaryMessage, ...recentMessages];

          // Update last_summarized_total_messages_count in the database
          await supabase
            .from('chats')
            .update({ last_summarized_total_messages_count: currentMessages.length })
            .eq('id', chatId);
          
          // Update the local chat store as well
          updateChat(chatId, { lastSummarizedTotalMessagesCount: currentMessages.length });

        } catch (e: any) {
          console.error("Memory compression failed:", e);
          // If summarization fails, still keep only recent messages to prevent context window overflow
          historyForAI = historyForAI.slice(-RECENT_MESSAGES_COUNT);
        } finally {
          set({ isMemoryUpdating: false });
        }
      }

      if (kbIdFromInput) {
        set({ isUsingKnowledgeBase: true });
        
        const addSystemMessage = async (sysContent: string) => {
          if (!chatId) return;
          const systemMessage = {
            id: uuidv4(),
            chatId,
            content: sysContent,
            role: 'system' as const,
            sender: 'system',
            timestamp: new Date().toISOString(),
          };
          addMessageLocally(chatId, systemMessage);
          historyForAI.push(systemMessage);
        };
      
        const searchResult = await performKnowledgeBaseSearch(kbIdFromInput, userQuery, addSystemMessage);
      
        if (searchResult.found && searchResult.contextContent) {
          const systemPrompt = `You are an AI assistant. Use the following documents from the knowledge base "${searchResult.kbName || 'Unnamed'}" to answer the user's question. Base your answer only on the provided context. If the answer is not in the context, say you could not find the information.\n\nCONTEXT:\n${searchResult.contextContent}`;
          const systemMessageForAI: Message = {
            id: uuidv4(),
            role: 'system',
            content: systemPrompt,
            sender: 'system',
            timestamp: new Date().toISOString(),
            chatId,
          };
          historyForAI.unshift(systemMessageForAI);
        } else if (searchResult.message) {
          const systemMessageForAI: Message = {
            id: uuidv4(),
            role: 'system',
            content: searchResult.message,
            sender: 'system',
            timestamp: new Date().toISOString(),
            chatId,
          };
          historyForAI.unshift(systemMessageForAI);
        }
      } else {
        set({ isUsingKnowledgeBase: false });
      }

      // Nudge model to use tools for web search/scrape when the user intent clearly asks for it
      const lowerQuery = userQuery.toLowerCase();
      const wantsWebSearch =
        lowerQuery.startsWith('find me ') ||
        lowerQuery.startsWith('search for ') ||
        lowerQuery.includes('web search') ||
        lowerQuery.includes('google ') ||
        lowerQuery.match(/\b(find|search|lookup)\b.*\b(in|near|for)\b/);

      if (wantsWebSearch) {
        const toolInstructionMessage: Message = {
          id: uuidv4(),
          chatId,
          role: 'system',
          sender: 'system',
          timestamp: new Date().toISOString(),
          content:
            'If the user\'s question would benefit from web search or reading a URL, output ONLY one JSON object:' +
            ' {"tool":"search_web","args":{"query":"..."} } or {"tool":"scrape_url","args":{"url":"..."} }.' +
            ' Do not include any other text before or after the JSON.',
        };
        historyForAI.unshift(toolInstructionMessage);
      }
      
      const tempUserMessageId = uuidv4();
      const userMessage: Message = {
        id: tempUserMessageId,
        chatId,
        content: userQuery,
        role: 'user',
        sender: user.id,
        timestamp: new Date().toISOString(),
        attachments,
      };
      
      historyForAI.push(userMessage);
      
      const tempAiMessageId = uuidv4();
      addMessageLocally(chatId, userMessage);
      addMessageLocally(chatId, {
        id: tempAiMessageId,
        chatId,
        content: '...',
        role: 'assistant',
        sender: 'ai',
        timestamp: new Date().toISOString(),
        model: selectedModelId,
      });
      
      try {
        // Step 1: First AI call to check for tool usage
        // UPDATED: Only run tool-check for non-local models AND when the user asks for web search
        const shouldToolCheck = wantsWebSearch && selectedModel.provider !== 'local';
        const toolCheckResponse = await sendMessageToModel(historyForAI, selectedModel, models, undefined, shouldToolCheck);
        let finalContent = sanitizeModelOutput(toolCheckResponse.content);
        let tokensUsed = toolCheckResponse.tokensUsed || 0;

        let toolCall = null;
        if (shouldToolCheck) {
          const jsonMatch = finalContent.match(/\{[\s\S]*"tool"[\s\S]*\}/);
          if (jsonMatch && jsonMatch[0]) {
            try {
              toolCall = JSON.parse(jsonMatch[0]);
            } catch (e) {
              console.error("Failed to parse tool call JSON:", e);
              toolCall = null;
            }
          }
        }

        // Step 2: If a tool is requested, execute it and make a second AI call
        if (shouldToolCheck && toolCall && toolCall.tool && toolCall.args) {
          let toolResult = '';
          let inProgressMessage = '';

          try {
            if (toolCall.tool === 'search_web' && toolCall.args.query) {
              inProgressMessage = `Searching the web for "${toolCall.args.query}"...`;
              updateMessageLocally(chatId, tempAiMessageId, { ...get().messages.find(m => m.id === tempAiMessageId)!, content: inProgressMessage });

              const { data, error } = await supabase.functions.invoke('web-search', { body: { query: toolCall.args.query } });
              if (error) throw new Error(error.message);
              
              const searchResults = data.results;
              let summary = `Web search results for "${toolCall.args.query}":\n\n`;
              if (searchResults.organic_results && searchResults.organic_results.length > 0) {
                summary += searchResults.organic_results.slice(0, 5).map((r: any) => `Title: ${r.title}\nLink: ${r.link}\nSnippet: ${r.snippet}\n---`).join('\n');
              } else if (searchResults.answer_box) {
                summary += `Answer Box: ${searchResults.answer_box.snippet || searchResults.answer_box.answer}\n`;
              } else {
                summary = "No relevant search results found.";
              }
              toolResult = summary;

            } else if (toolCall.tool === 'scrape_url' && toolCall.args.url) {
              inProgressMessage = `Reading content from ${toolCall.args.url}...`;
              updateMessageLocally(chatId, tempAiMessageId, { ...get().messages.find(m => m.id === tempAiMessageId)!, content: inProgressMessage });

              const { data, error } = await supabase.functions.invoke('scrape-url', { body: { url: toolCall.args.url } });
              if (error) throw new Error(error.message);
              toolResult = `Content from ${toolCall.args.url}:\n\n${data.content}`;
            } else {
              throw new Error("AI requested an invalid or incomplete tool.");
            }

            // Step 3: Second AI call for synthesis
            const synthesisPrompt: Message = {
              id: uuidv4(),
              chatId,
              role: 'system',
              sender: 'system',
              content: `Here is the information you requested. Use it to answer the user's original question.\n\n${toolResult}`,
              timestamp: new Date().toISOString(),
            };
            const synthesisHistory = [...historyForAI, synthesisPrompt];
            const synthesisResponse = await sendMessageToModel(synthesisHistory, selectedModel, models);
            finalContent = sanitizeModelOutput(synthesisResponse.content);
            tokensUsed += synthesisResponse.tokensUsed || 0;

          } catch (toolError: any) {
            finalContent = `Error using tool: ${toolError.message}`;
          }
        }
      
        const persistedUserMessage = await addMessageToDb(chatId, {
          content: userQuery,
          role: 'user',
          sender: user.id,
          attachments,
        });
      
        if (!persistedUserMessage) {
          throw new Error('Failed to save your message to the database.');
        }
      
        updateMessageLocally(chatId, tempUserMessageId, persistedUserMessage);
      
        const persistedAiMessage = await addMessageToDb(chatId, {
          content: finalContent,
          role: 'assistant',
          sender: 'ai',
          model: selectedModelId,
        });
      
        if (!persistedAiMessage) {
          throw new Error('Failed to save the AI response to the database.');
        }
      
        updateMessageLocally(chatId, tempAiMessageId, persistedAiMessage);
      
        if (tokensUsed > 0) {
          useSupabaseStore.getState().addTokensUsed(chatId, tokensUsed);
        }
      
      } catch (error: any) {
        console.error("Error sending message:", error);
        toast({
          title: "Error",
          description: error.message || "Failed to send message. Please try again.",
          variant: "destructive",
        });
        set({ error: error.message });
      
        useSupabaseStore.setState(state => ({
          chats: state.chats.map(chat =>
            chat.id === chatId
              ? {
                  ...chat,
                  messages: chat.messages.filter(
                    msg => msg.id !== tempUserMessageId && msg.id !== tempAiMessageId
                  ),
                }
              : chat
          ),
        }));
      } finally {
        set({ isLoading: false });
      }
    },

    handleTitleChange: async (newTitle) => {
      const { chatId } = get();
      const { updateChatTitle } = useSupabaseStore.getState();
      if (chatId && newTitle) {
        try {
          await updateChatTitle(chatId, newTitle);
          set({ chatTitle: newTitle });
          toast({ title: "Success", description: "Chat title updated." });
        } catch (error: any) {
          toast({ title: "Error", description: error.message, variant: "destructive" });
        }
      }
    },

    handleDeleteChat: async () => {
      const { chatId } = get();
      const { deleteChat } = useSupabaseStore.getState();
      if (chatId) {
        try {
          await deleteChat(chatId);
          toast({ title: "Chat deleted" });
        } catch (error: any) {
          toast({ title: "Error", description: error.message, variant: "destructive" });
        }
      }
    },

    handleModelChange: async (modelId: string) => {
      const { chatId } = get();
      const { models } = useSupabaseStore.getState(); // Get models from the main store
      const { updateChatModel } = useSupabaseStore.getState();
      if (chatId) {
        try {
          await updateChatModel(chatId, modelId);
          const model = models.find(m => m.id === modelId);
          set({ selectedModelId: modelId, modelName: model?.name || 'Unknown Model' });
          toast({ title: "Model Updated", description: `Switched to ${model?.name}` });
        } catch (error: any) {
          toast({ title: "Error", description: `Failed to switch model: ${error.message}`, variant: "destructive" });
        }
      }
    },

    handleModel2Change: async (modelId: string) => {
      const { chatId } = get();
      const { models } = useSupabaseStore.getState(); // Get models from the main store
      const { updateChat } = useSupabaseStore.getState();
      if (chatId) {
        try {
          set({ modelId2: modelId });
          updateChat(chatId, { modelId2: modelId });

          if (useAppStore.getState().saveHistory) {
            await supabase.from('chats').update({ model_id_2: modelId }).eq('id', chatId);
          }
          
          const model = models.find(m => m.id === modelId);
          toast({ title: "Second Model Updated", description: `Switched to ${model?.name}` });
        } catch (error: any) {
          toast({ title: "Error", description: `Failed to switch second model: ${error.message}`, variant: "destructive" });
        }
      }
    },

    clearMessages: () => {
      const { chatId } = get();
      if (!chatId) return;
      useSupabaseStore.getState().set(state => ({
        chats: state.chats.map(c => c.id === chatId ? { ...c, messages: [] } : c)
      }));
      set({ messages: [] });
      toast({ title: "Chat cleared" });
    },
  }
});